/**
 * WebSocket-backed submission detail hook.
 *
 * Composes over `useSubmission` (which handles the initial snapshot + polling
 * fallback) and layers live JudgeStreamEvent updates on top by patching the
 * React Query cache as events arrive.
 *
 * Design notes:
 *   - WS is an accelerator, not a replacement. Polling stays on as a safety
 *     net if the WS drops mid-submission.
 *   - The DO keeps a recent-events ring buffer, so reconnecting mid-flight
 *     replays what we missed (up to 64 events).
 *   - On `finished`, we close the WS and force one final query invalidate so
 *     R2-backed detail (sample case input/expected/actual) is fetched.
 *   - We do NOT auto-reconnect on close — if it drops, polling handles it.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  isTerminalVerdict,
  type JudgeStreamEvent,
  type SubmissionCaseResult,
  type SubmissionDetail,
} from '@flarecode/shared';
import { submissionStreamUrl } from '../lib/api';
import { useSubmission } from './useSubmission';

export interface UseSubmissionStreamResult {
  submission: SubmissionDetail | undefined;
  isLoading: boolean;
  error: unknown;
  /** Total case count from the `started` event; undefined until received. */
  totalCases: number | undefined;
  /** True while the judge is compiling the user's source. */
  isCompiling: boolean;
  /** True if the WS is currently open. */
  streamConnected: boolean;
}

export function useSubmissionStream(id: string | undefined): UseSubmissionStreamResult {
  const { submission, isLoading, error } = useSubmission(id);
  const queryClient = useQueryClient();

  const [totalCases, setTotalCases] = useState<number | undefined>(undefined);
  const [isCompiling, setIsCompiling] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);

  useEffect(() => {
    if (!id) return;
    // Don't open the WS if the cache already says this is terminal — saves a
    // connection for completed submissions being revisited.
    const cached = queryClient.getQueryData<SubmissionDetail>(['submission', id]);
    if (cached && isTerminalVerdict(cached.verdict)) return;

    let ws: WebSocket | null = null;
    let cancelled = false;

    try {
      ws = new WebSocket(submissionStreamUrl(id));
    } catch (err) {
      console.warn('submission stream: failed to open', err);
      return;
    }

    ws.addEventListener('open', () => {
      if (cancelled) return;
      setStreamConnected(true);
    });

    ws.addEventListener('message', (ev) => {
      if (cancelled) return;
      let event: JudgeStreamEvent;
      try {
        event = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as JudgeStreamEvent;
      } catch {
        return;
      }
      applyEvent(queryClient, id, event, {
        setTotalCases,
        setIsCompiling,
      });
      // On terminal verdict, force one authoritative fetch to pull any
      // R2-backed fields (sample case input/expected/actual, compile stderr)
      // that aren't in the stream events.
      if (event.type === 'finished' || event.type === 'compile_failed') {
        queryClient.invalidateQueries({ queryKey: ['submission', id] });
      }
    });

    ws.addEventListener('close', () => {
      if (cancelled) return;
      setStreamConnected(false);
      setIsCompiling(false);
    });

    ws.addEventListener('error', () => {
      // Polling is still active via useSubmission; no manual recovery needed.
      if (cancelled) return;
      setStreamConnected(false);
    });

    return () => {
      cancelled = true;
      setStreamConnected(false);
      setIsCompiling(false);
      try {
        ws?.close();
      } catch {
        // ignore
      }
    };
  }, [id, queryClient]);

  return { submission, isLoading, error, totalCases, isCompiling, streamConnected };
}

/**
 * Patch the cached SubmissionDetail based on an incoming stream event.
 *
 * Events convey state transitions, not full snapshots. We merge each event
 * onto whatever the current cache says. If no cache yet (initial fetch hasn't
 * landed), we skip — the fetch will populate authoritatively, and the DO's
 * ring buffer will replay any events we missed on reconnect.
 */
function applyEvent(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  event: JudgeStreamEvent,
  setters: {
    setTotalCases: (n: number | undefined) => void;
    setIsCompiling: (b: boolean) => void;
  },
): void {
  const key = ['submission', id] as const;
  queryClient.setQueryData<SubmissionDetail | undefined>(key, (prev) => {
    if (!prev) {
      // No snapshot yet. Let the initial fetch populate it; the ring buffer
      // replay on reconnect or the upcoming poll tick will carry state.
      return prev;
    }
    switch (event.type) {
      case 'queued':
        return prev.verdict === 'pending' ? prev : { ...prev, verdict: 'pending' };
      case 'started':
        return prev.verdict === 'running' ? prev : { ...prev, verdict: 'running' };
      case 'compiling':
        return prev.verdict === 'running' ? prev : { ...prev, verdict: 'running' };
      case 'compile_failed':
        return {
          ...prev,
          verdict: 'compile_error',
          compileStderr: event.stderr,
        };
      case 'case_result': {
        const nextCase: SubmissionCaseResult = {
          index: event.caseIndex,
          passed: event.passed,
          verdict: event.verdict,
          runtimeMs: event.runtimeMs,
          memoryKb: event.memoryKb,
        };
        const cases = [...prev.cases];
        const existingIdx = cases.findIndex((c) => c.index === event.caseIndex);
        if (existingIdx === -1) {
          cases.push(nextCase);
          cases.sort((a, b) => a.index - b.index);
        } else {
          // Preserve any previously-populated sample detail if it exists
          // (the stream event doesn't carry input/expected/actual).
          cases[existingIdx] = { ...cases[existingIdx], ...nextCase };
        }
        return { ...prev, cases };
      }
      case 'finished':
        return {
          ...prev,
          verdict: event.verdict,
          runtimeMs: event.runtimeMs,
          memoryKb: event.memoryKb,
          failedCaseIndex: event.failedCaseIndex,
        };
      default:
        return prev;
    }
  });

  // Side-effect state (not part of SubmissionDetail)
  switch (event.type) {
    case 'started':
      setters.setTotalCases(event.totalCases);
      break;
    case 'compiling':
      setters.setIsCompiling(true);
      break;
    case 'compile_failed':
    case 'finished':
      setters.setIsCompiling(false);
      break;
  }
}
